import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/topupStyle';
import { HeadingSBold } from 'components/Typography/Typography';
import CustomButton from 'components/CustomButtons/Button';
import prepareData from 'views/TopUp/helpers/prepare';
import { translate } from 'components/Translation';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ThankYou from 'views/TopUp/subcomponents/common/ThankYou';
import PaymentForm from 'components/PaymentForm';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { useSelector } from 'react-redux';
import { isSmartPay } from 'utils/killswitch';

// @ts-ignore
const useStyles = makeStyles(styles);

interface PaymentProps {
	selectedElements: types.topUp.INumber[];
	totalAmount: number;
	handleTopUp: (data: any) => void;
	topUpError?: string | null;
	formData: types.payment.IFormData;
	setFormCardData: types.payment.setFormData;
	setTokenData: (token: IPaymentToken) => void;
	checkValidity: boolean;
	setCheckValidity: (checkValidity: boolean) => void;
	selectedDonation: types.payment.IDonation | null;
	topUpLimit: types.payment.paymentLimitReached;
	onBack: () => void;
	toggleSaveCard: () => void;
	setSaveCard: (saveCard: boolean) => void;
}

const Payment = ({
	selectedElements,
	totalAmount,
	handleTopUp,
	topUpError,
	formData,
	setFormCardData,
	setTokenData,
	checkValidity,
	setCheckValidity,
	selectedDonation,
	topUpLimit,
	toggleSaveCard,
	setSaveCard,
	onBack,
}: PaymentProps) => {
	const classes = useStyles();
	const [validation, setValidation] = useState<types.payment.IValidation>({
		number: { isValid: true, isPotentiallyValid: true, card: null },
		cvv: { isValid: true, isPotentiallyValid: true },
		date: { isValid: true, isPotentiallyValid: true },
		email: { isValid: true, isPotentiallyValid: true },
	});
	const remoteConfig = useSelector(remoteConfigSelector);
	const DISABLE_SMART_PAY = !isSmartPay(remoteConfig);

	const payEnabled = useMemo(() => {
		const formDataCheck = !emailNotValid(formData['email']);
		if (formData.type === 'token') {
			return formDataCheck;
		} else {
			return (
				formDataCheck &&
				Object.keys(validation).every((key) => validation[key].isPotentiallyValid) &&
				['number', 'cvv', 'holder'].every((key) => formData[key].length > 0)
			);
		}
	}, [formData, validation]);

	const isValid = useMemo(() => {
		if (formData.type === 'token') return payEnabled;
		else return payEnabled && Object.keys(validation).every((key) => validation[key].isValid);
	}, [validation, formData.type, payEnabled]);

	return (
		<div className={classNames(classes.wrapper, classes.donationTabWrapper)} id="payment-container">
			<div className={classes.donationTabHeader}>
				<HeadingSBold disableMargin>{translate('topup.payment.header')}</HeadingSBold>
			</div>
			<ConditionalRender
				show={!!selectedDonation}
				onTrue={() => <ThankYou selectedDonation={selectedDonation!} />}
			/>
			<PaymentForm
				selectedDonation={selectedDonation}
				totalAmount={totalAmount}
				formData={formData}
				setFormData={setFormCardData}
				numberOfElements={selectedElements.length}
				paymentLimitReached={topUpLimit}
				checkValidity={checkValidity}
				validation={validation}
				setValidation={setValidation}
				paymentError={topUpError}
				setTokenData={setTokenData}
				toggleSaveCard={toggleSaveCard}
				setSaveCard={setSaveCard}
			/>
			<div className={classes.donationsButtons}>
				<CustomButton
					onClick={() => {
						onBack();
					}}
					color="tertiary-light"
				>
					{translate('back')}
				</CustomButton>
				<div>
					<CustomButton
						onClick={() => {
							setCheckValidity(true);
							if (isValid) {
								handleTopUp(
									prepareData(
										validation,
										formData,
										selectedElements,
										selectedDonation,
										DISABLE_SMART_PAY
									)
								);
							}
						}}
						color="primary-light"
						disabled={!payEnabled}
						fullWidth
					>
						{translate('pay')}
					</CustomButton>
				</div>
			</div>
		</div>
	);
};

export default Payment;
